import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Routes, Route, Navigate } from 'react-router-dom';
import { DashboardSidebar } from '../components/DashboardSidebar';
import { SupportTicketPanel } from '../components/SupportTicketPanel';
import { Server, CreditCard, LifeBuoy, Activity, Download, ExternalLink, PlusCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { CustomerService, Invoice } from '../context/AppContext';
import { ServiceDetailCard } from '../components/ServiceDetailCard';
export function CustomerDashboard() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const {
    customerUser
  } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [services, setServices] = useState<CustomerService[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  useEffect(() => {
    async function loadData() {
      if (!customerUser?.id) return;
      try {
        // Fetch customer record
        const {
          data: customerData,
          error: customerError
        } = await supabase.from('customers').select('services').eq('id', customerUser.id).maybeSingle();
        if (customerError) throw customerError;
        if (customerData?.services) {
          setServices(customerData.services as CustomerService[]);
        }
        // Fetch invoices
        const {
          data: invoiceData,
          error: invoiceError
        } = await supabase.from('invoices').select('*').eq('client_email', customerUser.email).order('created_at', {
          ascending: false
        }).limit(5);
        if (!invoiceError && invoiceData) {
          // Map snake_case DB columns to camelCase Invoice interface
          const mappedInvoices = invoiceData.map(inv => ({
            id: inv.id,
            clientName: inv.client_name,
            clientEmail: inv.client_email,
            clientType: inv.client_type,
            items: inv.items,
            total: inv.total,
            status: inv.status,
            createdAt: inv.created_at,
            dueDate: inv.due_date
          })) as Invoice[];
          setInvoices(mappedInvoices);
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    }
    loadData();
  }, [customerUser]);
  const activeServicesCount = services.filter(s => s.status === 'active').length;
  const nextInvoiceAmount = services.filter(s => s.status === 'active').reduce((sum, s) => sum + (s.price || 0), 0);
  const stats = [{
    label: 'Active Services',
    value: activeServicesCount.toString(),
    icon: Server,
    color: 'text-blue-500',
    bg: 'bg-blue-500/10'
  }, {
    label: 'Next Invoice',
    value: nextInvoiceAmount > 0 ? `$${nextInvoiceAmount.toFixed(2)}` : 'None',
    icon: CreditCard,
    color: 'text-fox-500',
    bg: 'bg-fox-500/10'
  }, {
    label: 'Support Tickets',
    value: '0',
    icon: LifeBuoy,
    color: 'text-amber-500',
    bg: 'bg-amber-500/10'
  }, {
    label: 'Uptime',
    value: '99.9%',
    icon: Activity,
    color: 'text-emerald-500',
    bg: 'bg-emerald-500/10'
  }];
  const Overview = () => {
    if (isLoading) {
      return <div className="flex items-center justify-center h-64" data-id="element-2321">
          <div className="w-8 h-8 border-2 border-fox-500/30 border-t-fox-500 rounded-full animate-spin" data-id="element-2322" />
        </div>;
    }
    return <>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-10" data-id="element-2323">
          <h1 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-2324">
            Welcome back, {customerUser?.name?.split(' ')[0] || 'Customer'}!
          </h1>
          <p className="text-slate-400" data-id="element-2325">
            Here's an overview of your hosting account.
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10" data-id="element-2326">
          {stats.map((stat, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.1
        }} className="bg-slate-950 border border-slate-800 rounded-2xl p-6" data-id="element-2327">
              <div className="flex items-center justify-between mb-4" data-id="element-2328">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.bg} ${stat.color}`} data-id="element-2329">
                  <stat.icon size={24} data-id="element-2330" />
                </div>
              </div>
              <h3 className="text-3xl font-bold text-white mb-1" data-id="element-2331">
                {stat.value}
              </h3>
              <p className="text-slate-400 text-sm font-medium" data-id="element-2332">{stat.label}</p>
            </motion.div>)}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2333">
          {/* Services List */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="lg:col-span-2 space-y-6" data-id="element-2334">
            <div className="flex items-center justify-between" data-id="element-2335">
              <h2 className="text-xl font-heading font-bold text-white" data-id="element-2336">
                My Services
              </h2>
              {services.length > 0 && <button className="text-sm text-fox-500 hover:text-fox-400 font-medium" data-id="element-2337">
                  View All
                </button>}
            </div>

            <div className="space-y-4" data-id="element-2338">
              {services.length === 0 ? <div className="bg-slate-950 border border-slate-800 rounded-2xl p-8 text-center flex flex-col items-center justify-center" data-id="element-2339">
                  <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center mb-4" data-id="element-2340">
                    <Server size={24} className="text-slate-500" data-id="element-2341" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-2342">
                    No active services
                  </h3>
                  <p className="text-slate-400 mb-6 max-w-sm" data-id="element-2343">
                    You don't have any active services yet. Browse our plans to
                    get started!
                  </p>
                  <Link to="/#services" className="flex items-center gap-2 bg-fox-600 hover:bg-fox-500 text-white px-5 py-2.5 rounded-lg font-medium transition-colors" data-id="element-2344">
                    <PlusCircle size={18} data-id="element-2345" />
                    Browse Services
                  </Link>
                </div> : services.map((service, i) => <ServiceDetailCard key={i} service={service} index={i} data-id="element-2346" />)}
            </div>
          </motion.div>

          {/* Recent Invoices */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="space-y-6" data-id="element-2347">
            <div className="flex items-center justify-between" data-id="element-2348">
              <h2 className="text-xl font-heading font-bold text-white" data-id="element-2349">
                Recent Invoices
              </h2>
            </div>

            <div className="bg-slate-950 border border-slate-800 rounded-2xl overflow-hidden" data-id="element-2350">
              {invoices.length === 0 ? <div className="p-8 text-center flex flex-col items-center justify-center" data-id="element-2351">
                  <div className="w-12 h-12 bg-slate-900 rounded-full flex items-center justify-center mb-3" data-id="element-2352">
                    <CreditCard size={20} className="text-slate-500" data-id="element-2353" />
                  </div>
                  <p className="text-slate-400 text-sm" data-id="element-2354">No invoices yet.</p>
                </div> : <>
                  <div className="divide-y divide-slate-800" data-id="element-2355">
                    {invoices.map((invoice, i) => <div key={i} className="p-4 flex items-center justify-between hover:bg-slate-900/50 transition-colors" data-id="element-2356">
                        <div data-id="element-2357">
                          <p className="text-sm font-medium text-white mb-1" data-id="element-2358">
                            {invoice.id.split('-')[0]}-
                            {invoice.id.split('-')[1]}
                          </p>
                          <p className="text-xs text-slate-400" data-id="element-2359">
                            {new Date(invoice.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-4" data-id="element-2360">
                          <span className="text-sm font-medium text-white" data-id="element-2361">
                            ${invoice.total.toFixed(2)}
                          </span>
                          <button className="text-slate-400 hover:text-fox-500 transition-colors" data-id="element-2362">
                            <Download size={18} data-id="element-2363" />
                          </button>
                        </div>
                      </div>)}
                  </div>
                  <div className="p-4 border-t border-slate-800 bg-slate-900/50" data-id="element-2364">
                    <button className="w-full text-sm text-center text-fox-500 hover:text-fox-400 font-medium" data-id="element-2365">
                      View Billing History
                    </button>
                  </div>
                </>}
            </div>
          </motion.div>
        </div>
      </>;
  };
  return <div className="min-h-screen bg-slate-900 flex" data-id="element-2366">
      <DashboardSidebar type="customer" isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} data-id="element-2367" />

      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0" data-id="element-2368">
        <div className="p-6 md:p-10 max-w-6xl mx-auto" data-id="element-2369">
          <Routes data-id="element-2370">
            <Route path="/" element={<Overview data-id="element-2372" />} data-id="element-2371" />
            <Route path="/support" element={<SupportTicketPanel userType="customer" userName={customerUser?.name || 'Customer'} userEmail={customerUser?.email || ''} data-id="element-2374" />} data-id="element-2373" />
            <Route path="*" element={<Navigate to="/dashboard/customer" replace data-id="element-2376" />} data-id="element-2375" />
          </Routes>
        </div>
      </main>
    </div>;
}